import React, { useState } from 'react';
import { Card } from '../../../../../../components/Card/Card';
import { Heatmap, Cluster } from '../../../../../../components/Heatmap/Heatmap';
import { Stack } from '../../../../../../components/Stack/Stack';
import { t } from '../../../../../../locales/translate';
import { FormRangepicker } from '../../../../../../components/Form/Datepicker/FormRangepicker';
import { useQueryHandler } from '../../../../../../model/graphql';
import { graphql } from '../../../../../../generated';
import { Container } from '../../../../../../components/Container/Container';
import { Spinner } from '../../../../../../components/Spinner/Spinner';
import { NoData } from '../../../../../../components/NoData/NoData';
import { SalesStatsForPostalCode } from '../../../../../../generated/graphql';
import { useTask } from '../../../../../../model/data';
import { Icon } from '../../../../../../components/Icon/Icon';
import { runSalesPostcodeExport } from '../../../../../../model/exports';
import { Tooltip } from '../../../../../../components/Tooltip/Tooltip';

export const PRODUCT_POSTCODE_SALES = graphql(`
  query ProductPostcodeSales($id: ID!, $from: ISO8601DateTime, $to: ISO8601DateTime) {
    product(id: $id) {
      id
      salesStatsPerPostCode(from: $from, to: $to) {
        count
        postCode
        latitude
        longitude
        locationPrecision
      }
    }
  }
`);

export const CATEGORY_POSTCODE_SALES = graphql(`
  query CategoryPostcodeSales($id: ID!, $from: ISO8601DateTime, $to: ISO8601DateTime) {
    category(id: $id) {
      id
      salesStatsPerPostCode(from: $from, to: $to) {
        count
        postCode
        latitude
        longitude
        locationPrecision
      }
    }
  }
`);

type SaleType = 'product' | 'category';
type QueryType = typeof PRODUCT_POSTCODE_SALES | typeof CATEGORY_POSTCODE_SALES;

const queries: Record<SaleType, QueryType> = {
  product: PRODUCT_POSTCODE_SALES,
  category: CATEGORY_POSTCODE_SALES,
};

interface Props {
  type: SaleType;
  saleId: string;
}

export function SalesHeatmap({ saleId, type }: Props): JSX.Element {
  const [intervalValue, setIntervalValue] = useState<[Date | undefined, Date | undefined] | undefined>();
  const exportTask = useTask({ handleError: true });

  const { data, loading } = useQueryHandler(queries[type], {
    variables: {
      id: saleId,
      from: intervalValue?.[0],
      to: intervalValue?.[1],
    },
  });

  const typedSales: SalesStatsForPostalCode[] = data?.[type]?.salesStatsPerPostCode || [];

  const parsedData: Cluster[] = typedSales?.map((sales) => ({
    id: sales.postCode,
    count: sales.count,
    location: {
      lat: sales.latitude,
      long: sales.longitude,
    },
    isCountry: sales.locationPrecision === 'country',
  }));

  return (
    <Card
      title={t.page.event.salesByGeography}
      actions={
        <Icon
          name="download--sm"
          tooltip={t.actions.downloadAsCSV}
          loading={exportTask.loading}
          onClick={() =>
            exportTask.run(
              runSalesPostcodeExport({
                sellableId: saleId,
                sellableType: type,
                from: intervalValue?.[0],
                to: intervalValue?.[1],
              })
            )
          }
        />
      }
      tooltip={() => (
        <Tooltip
          reference={<Icon name="help--sm" color="var(--color-text-label)" />}
          position="bottom"
          fontSize="small"
          padding="medium"
          darkMode
          small
        >
          {t.page.event.salesByGeographyTooltip}
        </Tooltip>
      )}
    >
      <Stack>
        <Container isLeftAligned>
          <FormRangepicker
            value={intervalValue}
            onChange={setIntervalValue}
            placeholder={t.component.heatmap.allSales}
            icon="calendar"
          />
        </Container>

        {loading ? <Spinner /> : parsedData?.length ? <Heatmap data={parsedData} /> : <NoData />}
      </Stack>
    </Card>
  );
}
